import React, { useCallback, useEffect, useMemo, useState } from "react";

// API
import { getDrivers } from "../../api/driver";
import { getEquipments } from "../../api/equipment";
import { getDispatchRequirements } from "../../api/order";
import { getDeliveryCenterConfig } from "../../api/DeliveryCenter/deliveryCenter";

// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedOrderDriverId,
  setShowSelectOrderDriverModal,
} from "../../store/actions/driversSlice";
import {
  dispatchOrders,
  postOrderDriverRequestThunk,
  selectOrdersByOrdersToDispatch,
} from "../../store/actions/ordersSlice";

// APP COMPONENTS
import DispatchSummaryInfos from "./DispatchSummaryInfos";
import ShippingProvidersHorizontal from "./ShippingProvidersHorizontal";

// MUI COMPONENTS
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Chip,
  FormControl,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

// Autocomplete components
import {
  AutocompleteStyledPaper,
  AutocompleteStyledPopper,
  StyledAutocomplete,
} from "../styled/inputs/autocomplete";

// ICONS
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentIcon from "@mui/icons-material/Payment";
import WarningIcon from "@mui/icons-material/Warning";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// Animated Modal Components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../modals/AnimatedModal";
import UberService from "../../api/uber";
import DrinkConfirmationDialog from "./dialogs/DrinkConfirmationDialog";

const SetOrderDriverModal = ({
  show,
  closeModal,
  autoShowProviders = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const selectedOrderDriverId = useSelector(
    (state) => state.drivers.selectedOrderDriverId
  );

  const ordersToDispatch = useSelector(
    (state) => state.orders.ordersToDispatch
  );

  const ordersOrdersToDispatch = useSelector((state) =>
    selectOrdersByOrdersToDispatch(state)
  );

  const [quoteData, setQuoteData] = useState(null);
  const [showShippingProviders, setShowShippingProviders] = useState(false);
  const [showDrinkConfirmDialog, setShowDrinkConfirmDialog] = useState(false);

  const [configs, setConfigs] = useState(null);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [isEquipmentAutocompleteOpen, setIsEquipmentAutocompleteOpen] =
    useState(false);
  const [options, setOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const [selectedDriver, setSelectedDriver] = useState(null);

  const [driverError, setDriverError] = useState(null);
  const [equipmentError, setEquipmentError] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Card orders as the server sees them right now. The store's has_card is
  // false on every order that arrived over the socket (the payload is built
  // before the order's payments exist), so it is only a fallback for when the
  // request fails. null = not loaded.
  const [cardOrderIds, setCardOrderIds] = useState(null);

  const hasCardInOrders = cardOrderIds
    ? cardOrderIds.length > 0
    : ordersOrdersToDispatch.some((order) => order.has_card);

  const hasDrinkInOrders = ordersOrdersToDispatch.some(
    (order) => order.has_drink
  );

  useEffect(() => {
    return () => {
      dispatch(setSelectedOrderDriverId(null));
    };
  }, []);

  useEffect(() => {
    if (!isAutocompleteOpen) return;
    loadDrivers();
  }, [isAutocompleteOpen]);

  useEffect(() => {
    if (!isEquipmentAutocompleteOpen) return;
    loadEquipments();
  }, [isEquipmentAutocompleteOpen]);

  useEffect(() => {
    if (ordersToDispatch.length === 0) {
      closeModal();
    }
  }, [ordersToDispatch]);

  useEffect(() => {
    if (show) {
      setError(null);
      setDriverError(null);
      setEquipmentError(null);
      setQuoteData(null);
      setShowShippingProviders(autoShowProviders);
      setSelectedDriver(null);
      setSelectedEquipment(null);
      loadConfig();
    }
  }, [show, autoShowProviders]);

  const ordersToDispatchKey = ordersToDispatch.join(",");

  useEffect(() => {
    if (!show || ordersToDispatch.length === 0) return;

    let cancelled = false;
    setCardOrderIds(null);
    getDispatchRequirements(ordersToDispatch)
      .then(({ data }) => {
        if (!cancelled) setCardOrderIds(data?.card_order_ids || []);
      })
      .catch((err) => console.error(err));

    return () => {
      cancelled = true;
    };
  }, [show, ordersToDispatchKey]);

  const loadConfig = async () => {
    try {
      const { data } = await getDeliveryCenterConfig({
        config: "equipment_dispatch_control",
      });
      setConfigs(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadDrivers = async () => {
    try {
      setLoadingDrivers(true);
      if (!isAutocompleteOpen) return;

      const { data } = await getDrivers();
      data.sort((a, b) => a.name.localeCompare(b.name));

      setOptions(
        data.map((driver) => ({
          value: driver.driver_id,
          label: `${driver.easy_id} - ${driver.name}`,
          firstLetter: /[0-9]/.test(driver.name[0]) ? "0-9" : driver.name[0],
          isBlocked: driver.has_active_shipping_route,
          driver: driver,
        }))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const loadEquipments = async () => {
    try {
      setLoadingEquipments(true);
      const { data } = await getEquipments();

      setEquipmentOptions(
        data.map((equipment) => ({
          value: equipment.id,
          label: equipment.name,
          isBlocked: equipment.has_active_shipping_route,
          equipment: equipment,
        }))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEquipments(false);
    }
  };

  const toggleShippingProviders = () => {
    setShowShippingProviders((prev) => !prev);
  };

  const handleEquipmentChange = useCallback((e, value) => {
    if (value?.isBlocked) {
      e.preventDefault();
      return;
    }
    setSelectedEquipment(value);
    setEquipmentError(null);
  }, []);

  const handleSelectChange = useCallback(
    (e, value) => {
      if (value?.isBlocked) {
        e.preventDefault();
        return;
      }
      setSelectedDriver(value);
      dispatch(setSelectedOrderDriverId(value?.value));
      setDriverError(null);
    },
    [dispatch]
  );

  const registerOrder = useCallback(async () => {
    if (!quoteData?.quoteInfo?.quoteId) return;
    setIsSubmitting(true);

    const { merchant_id } = quoteData;

    try {
      if (quoteData.providerId === "ifood") {
        await dispatch(
          postOrderDriverRequestThunk({
            mid: merchant_id,
            ...quoteData.quoteInfo,
          })
        ).unwrap();
      }

      if (quoteData.providerId === "uber") {
        const deliveryData = {
          quote_id: quoteData.quoteInfo.quoteId,
          order_id: ordersToDispatch[0],
        };
        await UberService.createDelivery(deliveryData);
      }

      dispatch(setShowSelectOrderDriverModal(false));
      dispatch(setSelectedOrderDriverId(null));
      closeModal();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [quoteData, dispatch]);

  const submitForm = async () => {
    setIsSubmitting(true);

    if (quoteData?.selected) {
      return registerOrder();
    }

    try {
      if (!selectedOrderDriverId || selectedOrderDriverId === 0) {
        setDriverError("Selecione um motorista antes de continuar.");
        setIsSubmitting(false);
        return;
      } else {
        setDriverError(null);
      }

      if (configs?.equipment_dispatch_control) {
        if (hasCardInOrders && !selectedEquipment?.value) {
          setEquipmentError(
            "Selecione um equipamento válido antes de continuar."
          );
          setIsSubmitting(false);
          return;
        } else {
          setEquipmentError(null);
        }
      }

      setError(null);
      await dispatch(
        dispatchOrders({
          driverId: selectedOrderDriverId,
          ...(configs?.equipment_dispatch_control &&
            hasCardInOrders && {
              equipmentId: selectedEquipment.value,
            }),
        })
      ).unwrap();

      dispatch(setSelectedOrderDriverId(null));
      closeModal();
    } catch (e) {
      console.error(e);
      // `.unwrap()` rethrows the rejectWithValue payload (the raw backend data).
      // On a plan-limit block the global order-limit modal handles messaging, so
      // just close this modal instead of showing an inline generic error.
      const data = e?.response?.data || e;
      const isLimitReached =
        data?.error_code === "subscription_limit_reached" ||
        data?.details?.resource_type === "order";
      if (isLimitReached) {
        closeModal();
      } else if (data?.error_code === "equipment_required") {
        // The server found card orders the modal did not know about — show
        // the maquininha field so the operator can pick one and retry.
        setCardOrderIds(data.card_order_ids || []);
        setEquipmentError(data.error);
      } else if (data?.error_code?.startsWith?.("equipment_")) {
        setSelectedEquipment(null);
        setEquipmentError(data.error);
      } else {
        setError("Ocorreu um erro ao despachar o pedido. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDrinkDialogClose = () => {
    setShowDrinkConfirmDialog(false);
  };

  const handleDrinkDialogConfirm = () => {
    setShowDrinkConfirmDialog(false);
    submitForm();
  };

  const handleConfirm = () => {
    if (
      (!quoteData?.selected && !selectedOrderDriverId) ||
      selectedOrderDriverId === 0
    ) {
      setDriverError("Selecione um motorista antes de continuar.");
      return;
    }

    if (
      configs?.equipment_dispatch_control &&
      hasCardInOrders &&
      !selectedEquipment?.value
    ) {
      setEquipmentError("Selecione um equipamento válido antes de continuar.");
      return;
    }

    if (hasDrinkInOrders) {
      setShowDrinkConfirmDialog(true);
    } else {
      submitForm();
    }
  };

  const handleCloseModal = (e) => {
    closeModal(e);
    dispatch(setSelectedOrderDriverId(null));
    setError(null);
  };

  const showDriverForm = !showShippingProviders && !quoteData?.selected;

  // Build actions for the summary chat
  const dispatchActions = useMemo(() => {
    const list = [];

    if (ordersToDispatch.length === 1) {
      list.push({
        key: "shipping",
        label: showShippingProviders ? "Entrega Própria" : "Chamar Motoboy",
        icon: <OpenInNewIcon />,
        handler: toggleShippingProviders,
      });
    }

    list.push({
      key: "confirm",
      label: "Confirmar Despacho",
      icon: <CheckCircleIcon />,
      handler: handleConfirm,
    });

    list.push({
      key: "cancel",
      label: "Cancelar",
      icon: <KeyboardBackspaceIcon />,
      handler: handleCloseModal,
    });

    return list;
  }, [
    ordersToDispatch.length,
    showShippingProviders,
    selectedOrderDriverId,
    selectedEquipment,
    quoteData,
    isSubmitting,
    hasCardInOrders,
    configs,
  ]);

  return (
    <>
      <AnimatedModal
        isOpen={show}
        onClose={handleCloseModal}
        width="500px"
        maxWidth="95%"
        maxHeight="90vh"
        zIndex={10000}
        forceOnTop={true}
        fullScreenMobile
      >
        <ModalHeader
          title={`Despachar Pedido${ordersToDispatch.length > 1 ? "s" : ""}`}
          icon={LocalShippingIcon}
          onClose={handleCloseModal}
        />

        <ModalContent padding={0}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              height: { xs: "calc(100dvh - 64px)", sm: "80vh" },
              backgroundColor: alpha(theme.palette.grey[50], 0.5),
            }}
          >
            <DispatchSummaryInfos
              orderIds={ordersToDispatch}
              actions={dispatchActions}
            >
              {/* Shipping providers panel */}
              {ordersToDispatch.length === 1 && showShippingProviders && (
                <ShippingProvidersHorizontal
                  ordersToDispatch={ordersToDispatch}
                  quoteData={quoteData}
                  setQuoteData={setQuoteData}
                />
              )}

              {/* Driver & Equipment selection */}
              {showDriverForm && (
                <FormControl error={!!error} sx={{ width: "100%" }}>
                  <StyledAutocomplete
                    disablePortal
                    id="select-driver-to-deliver-order"
                    open={isAutocompleteOpen}
                    onOpen={() => setIsAutocompleteOpen(true)}
                    onClose={() => setIsAutocompleteOpen(false)}
                    loading={loadingDrivers}
                    options={options}
                    groupBy={(option) => option.firstLetter}
                    value={selectedDriver}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Selecione o motorista"
                        placeholder="Buscar motorista..."
                        variant="outlined"
                        error={!!driverError}
                        helperText={driverError}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 16px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              mr: 1.5,
                              bgcolor: option.isBlocked
                                ? alpha(theme.palette.warning.main, 0.2)
                                : alpha(theme.palette.primary.main, 0.2),
                              color: option.isBlocked
                                ? theme.palette.warning.main
                                : theme.palette.primary.main,
                            }}
                          >
                            {option.label[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.label}
                            </Typography>
                            {option.driver.phone && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.driver.phone}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {option.isBlocked && (
                          <Chip
                            label="Na Rua"
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </li>
                    )}
                    PopperComponent={(props) => (
                      <AutocompleteStyledPopper {...props} placement="top" />
                    )}
                    PaperComponent={(props) => (
                      <AutocompleteStyledPaper
                        {...props}
                        sx={{ mt: 0, mb: "4px" }}
                      />
                    )}
                    onChange={(event, value) => {
                      if (value?.isBlocked) {
                        event.preventDefault();
                        return;
                      }
                      handleSelectChange(event, value);
                    }}
                    sx={{ mb: 1.5 }}
                  />

                  {/* Equipment Selection */}
                  {configs?.equipment_dispatch_control && hasCardInOrders && (
                    <StyledAutocomplete
                      disablePortal
                      id="select-equipment-order-delivery"
                      open={isEquipmentAutocompleteOpen}
                      onOpen={() => setIsEquipmentAutocompleteOpen(true)}
                      onClose={() => setIsEquipmentAutocompleteOpen(false)}
                      loading={loadingEquipments}
                      options={equipmentOptions}
                      value={selectedEquipment}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Selecione a maquininha"
                          placeholder="Buscar por nome..."
                          variant="outlined"
                          error={!!equipmentError}
                          helperText={equipmentError}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PaymentIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                      PopperComponent={(props) => (
                        <AutocompleteStyledPopper {...props} placement="top" />
                      )}
                      PaperComponent={(props) => (
                        <AutocompleteStyledPaper
                          {...props}
                          sx={{ mt: 0, mb: "4px" }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 16px",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                mr: 1.5,
                                bgcolor: option.isBlocked
                                  ? alpha(theme.palette.warning.main, 0.2)
                                  : alpha(theme.palette.info.main, 0.2),
                                color: option.isBlocked
                                  ? theme.palette.warning.main
                                  : theme.palette.info.main,
                              }}
                            >
                              <PaymentIcon fontSize="small" />
                            </Avatar>
                            <Typography>{option.label}</Typography>
                          </Box>
                          {option.isBlocked && (
                            <Chip
                              label="Em Uso"
                              color="warning"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </li>
                      )}
                      onChange={handleEquipmentChange}
                    />
                  )}

                  {error && (
                    <Alert
                      severity="error"
                      icon={<WarningIcon fontSize="inherit" />}
                      sx={{ mt: 1, borderRadius: 1.5 }}
                    >
                      {error}
                    </Alert>
                  )}
                </FormControl>
              )}
            </DispatchSummaryInfos>
          </Box>
        </ModalContent>
      </AnimatedModal>

      <DrinkConfirmationDialog
        isOpen={showDrinkConfirmDialog}
        onClose={handleDrinkDialogClose}
        onConfirm={handleDrinkDialogConfirm}
      />
    </>
  );
};

export default SetOrderDriverModal;
